import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  CreditCard,
  Savings,
  AddCircleOutline,
  Autorenew,
  SupportAgent,
  HelpOutline,
  ArrowForward,
} from '@mui/icons-material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { useNavigate } from 'react-router-dom';
import {
  PAY_PER_USE_PRICING,
  SUBSCRIPTION_PLANS,
  CREDIT_VALUE_CZK,
  EXTRA_CREDIT_PRICE_CZK,
} from '../config/pricing';
import Footer from '../components/Footer';
import { subscriptionService } from '../services/subscriptionService';
import { getErrorMessage } from '../utils/errorUtils';
import { getStripeClient } from '../services/stripeClient';

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formatCurrency = (value: number) => value.toLocaleString('cs-CZ');
  const creditValue = CREDIT_VALUE_CZK.toLocaleString('cs-CZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const extraCreditPrice = EXTRA_CREDIT_PRICE_CZK.toLocaleString('cs-CZ', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const valueCards = [
    {
      icon: <CreditCard sx={{ fontSize: 28 }} />,
      title: 'Platba kredity',
      description:
        'Každá služba má pevně daný počet kreditů. Platba probíhá až po dokončení úkonu, takže neplatíte za rozpracované akce.',
    },
    {
      icon: <Autorenew sx={{ fontSize: 28 }} />,
      title: 'Přenos nevyužitých kreditů',
      description:
        'Kredity z tarifu lze převést do dalšího měsíce (maximálně jeden). Díky tomu nic nepropadá, když máte klidnější období.',
    },
    {
      icon: <AddCircleOutline sx={{ fontSize: 28 }} />,
      title: 'Dokup kdykoliv',
      description:
        `Potřebujete více? Další kredity dokoupíte za ${extraCreditPrice} Kč / kredit (včetně DPH), stále výhodněji než platba za jednotlivé úkony.`,
    },
  ];

  const faqItems = [
    {
      question: 'Jak funguje přepočet kreditů na koruny?',
      answer: `Základní převodní hodnota je 1 kredit = ${creditValue} Kč. Například konzultace stojí 200 kreditů, což odpovídá 50 Kč.`,
    },
    {
      question: 'Co se stane, když kredity vyčerpám?',
      answer:
        'Můžete dokoupit další kredity za 9 Kč/kredit. Zakoupené kredity se připíší okamžitě a můžete pokračovat bez přerušení.',
    },
    {
      question: 'Jaké jsou výhody předplatného?',
      answer:
        'Tarify START, Standard a Pro poskytují výrazně nižší jednotkovou cenu za kredit a navíc rezervu na častější využití. Nevyužité kredity lze převést do dalšího měsíce.',
    },
  ];

  const handleCheckout = async (planKey: string) => {
    setError(null);
    setLoadingPlan(planKey);
    try {
      const session = await subscriptionService.startCheckout(planKey);
      const stripe = await getStripeClient();

      if (stripe && session.session_id) {
        const { error: redirectError } = await stripe.redirectToCheckout({
          sessionId: session.session_id,
        });

        if (redirectError) {
          console.error('Stripe redirect failed', redirectError);
          if (session.checkout_url) {
            window.location.href = session.checkout_url;
            return;
          }
          throw redirectError;
        }
        return;
      }

      if (session.checkout_url) {
        window.location.href = session.checkout_url;
        return;
      }

      throw new Error('Stripe checkout nebyl správně nakonfigurován.');
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          'Nepodařilo se vytvořit platební relaci. Zkuste to prosím znovu.'
        )
      );
    } finally {
      setLoadingPlan(null);
    }
  };

  const handlePortal = async () => {
    setError(null);
    setPortalLoading(true);
    try {
      const session = await subscriptionService.openCustomerPortal();
      window.location.href = session.portal_url;
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          'Nepodařilo se otevřít zákaznický portál. Zkuste to prosím znovu.'
        )
      );
    } finally {
      setPortalLoading(false);
    }
  };

  const handleBuyCredits = async (credits: number) => {
    setError(null);
    setLoadingPlan('credits');
    try {
      const session = await subscriptionService.startCreditCheckout(credits);
      const stripe = await getStripeClient();
      if (stripe && session.session_id) {
        const { error: redirectError } = await stripe.redirectToCheckout({ sessionId: session.session_id });
        if (redirectError) {
          console.error('Stripe redirect failed', redirectError);
          if (session.checkout_url) {
            window.location.href = session.checkout_url;
            return;
          }
          throw redirectError;
        }
        return;
      }
      if (session.checkout_url) {
        window.location.href = session.checkout_url;
        return;
      }
      throw new Error('Stripe checkout nebyl správně nakonfigurován.');
    } catch (err) {
      setError(getErrorMessage(err, 'Nepodařilo se vytvořit platební relaci pro kredity. Zkuste to prosím znovu.'));
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#F2F2F2' }}>
      <Container
        maxWidth={false}
        sx={{
          maxWidth: '1920px',
          px: { xs: 2, sm: 3, md: 4, lg: '276px' },
          pt: { xs: 10, sm: 12, md: 14 },
          pb: { xs: 6, md: 10 },
        }}
      >
        {/* Hero */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: { xs: '20px', md: '28px' },
            background: 'linear-gradient(135deg, rgba(31,31,31,0.95) 0%, rgba(58,58,58,0.9) 40%, rgba(231,47,13,0.85) 100%)',
            color: '#FFFFFF',
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 6, sm: 7, md: 8 },
            mb: { xs: 6, md: 8 },
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1, textAlign: { xs: 'left', md: 'center' }, maxWidth: '960px', mx: 'auto' }}>
            <Chip
              icon={<Savings sx={{ fontSize: 18 }} />}
              label="Kreditní systém a tarify"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: '#FFFFFF',
                fontWeight: 600,
                mb: 2,
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Inter',
                fontWeight: 800,
                fontSize: { xs: '32px', sm: '42px', md: '54px' },
                lineHeight: 1.1,
                mb: 2,
              }}
            >
              Ceník, kredity a předplatné
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '16px', sm: '18px', md: '20px' },
                lineHeight: 1.5,
                color: 'rgba(255,255,255,0.85)',
              }}
            >
              Jednotný kreditní systém (1 kredit = {creditValue} Kč) pro konzultace, analýzy a generování dokumentů. Vyberte si,
              zda platit jednotlivé úkony, nebo využít zvýhodněné měsíční tarify s větší rezervou.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                flexWrap: 'wrap',
                mt: 4,
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => void handleCheckout('standard')}
                endIcon={<ArrowForward />}
                disabled={Boolean(loadingPlan)}
                sx={{
                  backgroundColor: '#FFFFFF',
                  color: '#1F1F1F',
                  fontWeight: 700,
                  textTransform: 'none',
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.5, sm: 1.75 },
                  borderRadius: '999px',
                  boxShadow: '0 10px 30px rgba(255,255,255,0.25)',
                  '&:hover': {
                    backgroundColor: '#F5F5F5',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {loadingPlan ? 'Přesměrování…' : 'Vybrat tarif'}
              </Button>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => void handlePortal()}
                  disabled={portalLoading}
                  startIcon={<ManageAccountsIcon />}
                  sx={{
                    color: '#FFFFFF',
                    borderColor: 'rgba(255,255,255,0.5)',
                    textTransform: 'none',
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.5, sm: 1.75 },
                    borderRadius: '999px',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      borderColor: '#FFFFFF',
                    },
                  }}
                >
                  {portalLoading ? 'Otevírám portál…' : 'Spravovat předplatné'}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: { xs: 4, md: 6 } }}>
            {error}
          </Alert>
        )}

        {/* Credit basics */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '26px', sm: '32px' },
              color: '#1F1F1F',
              mb: 3,
              textAlign: { xs: 'left', md: 'center' },
            }}
          >
            Jak fungují kredity
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
              gap: { xs: 3, md: 4 },
            }}
          >
            {valueCards.map((card) => (
              <Paper
                key={card.title}
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4 },
                  height: '100%',
                  borderRadius: '20px',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(231,47,13,0.12)',
                    color: '#e72f0d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  {card.icon}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '18px', mb: 1 }}>{card.title}</Typography>
                <Typography sx={{ color: '#424242', lineHeight: 1.6, fontSize: '14px' }}>{card.description}</Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Pay-per-use pricing */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '26px', sm: '32px' },
              color: '#1F1F1F',
              mb: 3,
              textAlign: { xs: 'left', md: 'center' },
            }}
            >
              Jednorázové úkony (pay-as-you-go)
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 3, md: 4 },
            }}
          >
            {PAY_PER_USE_PRICING.map((item) => (
              <Paper
                key={item.key}
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: '20px',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#1F1F1F' }}>{item.name}</Typography>
                <Typography sx={{ color: '#424242', lineHeight: 1.6, fontSize: '14px' }}>{item.description}</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${formatCurrency(item.priceCZK)} Kč`}
                    sx={{ backgroundColor: '#1F1F1F', color: '#FFFFFF', fontWeight: 600 }}
                  />
                  <Chip
                    label={`${formatCurrency(item.credits)} kreditů`}
                    sx={{ backgroundColor: 'rgba(231,47,13,0.12)', color: '#e72f0d', fontWeight: 600 }}
                  />
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography sx={{ color: '#424242', fontSize: '13px' }}>
                  Platba se odečte po dokončení služby. Pokud úkon zrušíte dříve, kredity zůstávají na účtu.
                </Typography>
                <Box sx={{ mt: 'auto', pt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={() => handleBuyCredits(item.credits)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '12px',
                      backgroundColor: '#e72f0d',
                      '&:hover': { backgroundColor: '#c41f00' }
                    }}
                  >
                    Koupit {formatCurrency(item.credits)} kreditů
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Subscription plans */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '26px', sm: '32px' },
              color: '#1F1F1F',
              mb: 3,
              textAlign: { xs: 'left', md: 'center' },
            }}
            >
              Měsíční tarify s kredity
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 3, md: 4 },
            }}
          >
              {SUBSCRIPTION_PLANS.map((plan) => {
                const isPopular = plan.key === 'standard';
                const isCustom = plan.key === 'custom';
                return (
                <Paper
                  key={plan.key}
                  elevation={0}
                  sx={{
                    // Custom již nepřesahuje – chceme 4 karty v jedné řadě
                    p: { xs: 3.5, sm: 4 },
                    borderRadius: '22px',
                    backgroundColor: '#FFFFFF',
                    boxShadow: isPopular ? '0 20px 50px rgba(231,47,13,0.25)' : '0 12px 32px rgba(0,0,0,0.08)',
                    border: isPopular ? '2px solid rgba(231,47,13,0.35)' : '1px solid rgba(0,0,0,0.06)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                    {isPopular && (
                      <Chip
                        label="Nejoblíbenější"
                        sx={{
                          alignSelf: 'center',
                          backgroundColor: '#e72f0d',
                          color: '#FFFFFF',
                          fontWeight: 600,
                        }}
                      />
                    )}
                    <Typography sx={{ fontWeight: 700, fontSize: '20px', color: '#1F1F1F', textTransform: 'uppercase' }}>
                      {plan.name}
                    </Typography>
                    {plan.tagline && (
                      <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#e72f0d', mt: 0.5 }}>
                        {plan.tagline}
                      </Typography>
                    )}
                    <Typography sx={{ fontWeight: 800, fontSize: '32px', color: '#1F1F1F' }}>
                      {plan.priceCZK !== undefined ? (
                        <>
                          {formatCurrency(plan.priceCZK)} Kč <Typography component="span" sx={{ fontSize: '15px', fontWeight: 500 }}>/ měsíc</Typography>
                        </>
                      ) : (
                        plan.priceLabel || ''
                      )}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, color: '#e72f0d', fontSize: '16px' }}>
                      {plan.credits !== undefined
                        ? `${formatCurrency(plan.credits)} kreditů / měsíc`
                        : plan.creditsLabel || ''}
                    </Typography>
                    {plan.userLabel && (
                      <Typography sx={{ fontWeight: 500, fontSize: '13px', color: '#1F1F1F' }}>
                        {plan.userLabel}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        color: '#424242',
                        fontSize: '14px',
                        lineHeight: 1.6,
                        textAlign: isCustom ? 'center' : 'left'
                      }}
                    >
                      {plan.description}
                    </Typography>
                    {plan.usageExamples && plan.usageExamples.length > 0 && (
                      <>
                        <Typography sx={{ color: '#1F1F1F', fontWeight: 600, fontSize: '14px' }}>
                          Zhruba vystačí na:
                        </Typography>
                        <Box
                          component="ul"
                          sx={{
                            listStyle: 'disc',
                            pl: 3,
                            m: 0,
                            color: '#424242',
                            fontSize: '14px',
                            lineHeight: 1.6,
                            '& > li': { mb: 0.5 }
                          }}
                        >
                          {plan.usageExamples.map((example) => (
                            <Box component="li" key={example}>
                              {example}
                            </Box>
                          ))}
                        </Box>
                      </>
                    )}
                    <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                      <Button
                        fullWidth
                        variant={'outlined'}
                        onClick={() => {
                          if (isCustom) {
                            navigate('/kontakty');
                          } else {
                            void handleCheckout(plan.key);
                          }
                        }}
                        disabled={loadingPlan !== null && !isCustom}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '12px',
                          fontWeight: 600,
                          // Všechny karty sjednoceně jako outlined (včetně STANDARD a CUSTOM)
                          borderColor: '#1F1F1F',
                          color: '#1F1F1F',
                          '&:hover': { borderColor: '#e72f0d', color: '#e72f0d' },
                        }}
                      >
                        {isCustom
                          ? 'Domluvit konzultaci'
                          : loadingPlan === plan.key
                            ? 'Přesměrování…'
                            : 'Vybrat tarif'}
                      </Button>
                    </Box>
                  </Paper>
                );
            })}
          </Box>
        </Box>

        {/* Additional info */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: '20px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '20px', color: '#1F1F1F', mb: 2 }}>
              Další informace
            </Typography>
            <List sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}><CheckCircle sx={{ color: '#4CAF50' }} /></ListItemIcon>
                <ListItemText
                  primary="Okamžité připsání kreditů"
                  secondary="Kredity se připíší ihned po úspěšné platbě a můžete je hned použít."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}><Savings sx={{ color: '#f9a825' }} /></ListItemIcon>
                <ListItemText
                  primary="Daňové doklady"
                  secondary="Okamžitě po platbě vystavujeme fakturu s rozpisem kreditů a DPH."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}><SupportAgent sx={{ color: '#0288d1' }} /></ListItemIcon>
                <ListItemText
                  primary="Priority support"
                  secondary="Tarify Standard a Pro zahrnují přednostní podporu a rychlejší SLA."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}><Autorenew sx={{ color: '#ab47bc' }} /></ListItemIcon>
                <ListItemText
                  primary="Přenos kreditů"
                  secondary="Nečerpané kredity se přenášejí do dalšího měsíce (max. jeden měsíc)."
                />
              </ListItem>
            </List>
          </Paper>
        </Box>

        {/* One‑off credits section removed per request */}

        {/* FAQ */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '26px', sm: '32px' },
              color: '#1F1F1F',
              mb: 3,
              textAlign: { xs: 'left', md: 'center' },
            }}
          >
            Často kladené otázky
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 3, md: 4 },
            }}
          >
            {faqItems.map((item) => (
              <Paper
                key={item.question}
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: '20px',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 10px 28px rgba(0,0,0,0.08)',
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <HelpOutline sx={{ color: '#e72f0d' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1F1F1F' }}>{item.question}</Typography>
                </Box>
                <Typography sx={{ color: '#424242', lineHeight: 1.6, fontSize: '14px' }}>{item.answer}</Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Final CTA */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: '20px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '22px', sm: '26px' }, mb: 2, color: '#1F1F1F' }}>
            Připraveni začít?
          </Typography>
          <Typography sx={{ color: '#424242', fontSize: '15px', maxWidth: '520px', mx: 'auto', mb: 3 }}>
            Vyberte tarif nebo kupte první kredity a během několika minut získáte přístup ke konzultacím, analýzám smluv
            i generování dokumentů.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => void handleCheckout('standard')}
              disabled={Boolean(loadingPlan)}
              sx={{
                textTransform: 'none',
                borderRadius: '12px',
                px: { xs: 3, sm: 4 },
                py: { xs: 1.25, sm: 1.5 },
                backgroundColor: '#1F1F1F',
                '&:hover': { backgroundColor: '#e72f0d' },
              }}
            >
              {loadingPlan ? 'Přesměrování…' : 'Vybrat tarif'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/kontakty')}
              sx={{
                textTransform: 'none',
                borderRadius: '12px',
                px: { xs: 3, sm: 4 },
                py: { xs: 1.25, sm: 1.5 },
                borderColor: '#1F1F1F',
                color: '#1F1F1F',
                '&:hover': { borderColor: '#e72f0d', color: '#e72f0d' },
              }}
            >
              Kontaktovat obchod
            </Button>
          </Box>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default SubscriptionPage;
